import { createFileRoute } from "@tanstack/react-router";
import ArticleReplaceTileCopingDecking from "@/pages/ArticleReplaceTileCopingDecking";
import { buildHead } from "@/lib/seo";
import { FEATURED_IMAGE_REPLACE_TILE_COPING_DECKING } from "@/data/blogData";
import { FAQS_REPLACE_TILE_COPING_DECKING } from "@/data/replaceTileCopingDeckingData";

const PATH = "/blog/replace-pool-tile-coping-decking-during-resurfacing";
const CANONICAL = `https://precisionpaverspools.com${PATH}`;

// BlogPosting schema. The `image` field is included only once a verified
// featured-image URL has been supplied, so the structured data never
// references an empty value.
const blogPosting: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline:
    "Should You Replace Pool Tile, Coping, and Decking During Resurfacing?",
  description:
    "Learn when pool tile, coping, and decking should be replaced during resurfacing—and when sound components may remain as part of a coordinated remodel.",
  datePublished: "2026-10-17",
  dateModified: "2026-10-17",
  author: {
    "@type": "Organization",
    name: "Precision Pavers & Pool Design",
  },
  publisher: {
    "@type": "Organization",
    name: "Precision Pavers & Pool Design",
    url: "https://precisionpaverspools.com/",
  },
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": CANONICAL,
  },
};

if (FEATURED_IMAGE_REPLACE_TILE_COPING_DECKING) {
  blogPosting.image = FEATURED_IMAGE_REPLACE_TILE_COPING_DECKING;
}

const jsonLd: Record<string, unknown>[] = [
  blogPosting,
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://precisionpaverspools.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Blog",
        item: "https://precisionpaverspools.com/blog",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Should You Replace Pool Tile, Coping, and Decking During Resurfacing?",
        item: CANONICAL,
      },
    ],
  },
];

// FAQPage schema is emitted only when visible FAQs exist, so the schema
// always exactly matches the on-page FAQ content. During the preparation
// phase the shared FAQ array is empty, so no FAQPage is emitted yet.
if (FAQS_REPLACE_TILE_COPING_DECKING.length > 0) {
  jsonLd.push({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS_REPLACE_TILE_COPING_DECKING.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  });
}

export const Route = createFileRoute(
  "/blog/replace-pool-tile-coping-decking-during-resurfacing",
)({
  head: () =>
    buildHead({
      title: "Replace Tile, Coping & Decking During Resurfacing?",
      description:
        "Learn when pool tile, coping, and decking should be replaced during resurfacing—and when sound components may remain as part of a coordinated remodel.",
      path: PATH,
      ogType: "article",
      ogImage: FEATURED_IMAGE_REPLACE_TILE_COPING_DECKING || undefined,
      jsonLd,
    }),
  component: ArticleReplaceTileCopingDecking,
});
